import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../mainApp/features/DataSlice/dataSlice';
import { db } from '../firebase';
import { collection, addDoc, doc, onSnapshot } from 'firebase/firestore';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

// Animacja obrotu dla kółka
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Styl dla komponentu Spinner
const Spinner = styled.div`
  border: 3px solid #ffffff;
  border-top: 3px solid #179544;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: ${spin} 1s linear infinite;
`;

// StyledButton na podstawie stylów Button z styled.js
const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: ${({ isCheckout }) => (isCheckout ? 'uppercase' : 'none')};
  border-radius: 0.4rem;
  border: 0.1rem solid #179544;
  width: 90%;
  height: 3.5rem;
  background-color: transparent;
  font-size: 1.2rem;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  text-decoration: none;
  color: #ffffff;
  
  &:hover {
    background-color: #179544;
    color: #121212;
    
    ${Spinner} {
      border: 3px solid #121212;
      border-top: 3px solid #ffffff;
    }
  }
`;

// Nowy styl dla przycisku aktywnej subskrypcji
const ActiveButton = styled(StyledButton)`
  background-color: #179544;
  color: #121212;
  cursor: default;

  &:hover {
    background-color: #179544;
    color: #121212;
  }
`;

// Nowy styl dla przycisku zarządzania subskrypcją
const ManageButton = styled(StyledButton)`
  border: 0.1rem solid #ffffff;

  &:hover {
    background-color: #ffffff;
    color: #121212;
  }
`;

// Funkcje do zarządzania processedDocs w localStorage
const getProcessedDocs = (userId) => {
  const stored = localStorage.getItem(`processed_checkout_sessions_${userId}`);
  return stored ? new Set(JSON.parse(stored)) : new Set();
};

const addProcessedDoc = (userId, docId) => {
  const processedDocs = getProcessedDocs(userId);
  processedDocs.add(docId);
  localStorage.setItem(
    `processed_checkout_sessions_${userId}`,
    JSON.stringify([...processedDocs])
  );
};

export default function StripeCheckoutButton({
  priceId,
  isCurrent,
  isFuture,
  hasActiveSubscription,
  onManageSubscription,
}) {
  const user = useSelector(selectUser);
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);

  useEffect(() => {
    if (!user || !checkoutSessionId) return;

    const sessionRef = doc(db, 'Users', user.uid, 'checkout_sessions', checkoutSessionId);

    const unsubscribe = onSnapshot(sessionRef, (doc) => {
      const data = doc.data();
      if (data?.url) {
        window.location.assign(data.url);
      } else if (data?.error) {
        toast.error(`Błąd Stripe: ${data.error.message}`, { position: 'bottom-center' });
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user, checkoutSessionId]);

  const handleClick = async () => {
    if (!user) {
      toast.error('Musisz być zalogowany', { position: 'bottom-center' });
      return;
    }

    setIsLoading(true); // Włącz kółko ładowania w przycisku

    const baseURL = process.env.REACT_APP_BASE_URL || window.location.origin;
    
    try {
     
      const docRef = await addDoc(
        collection(db, 'Users', user.uid, 'checkout_sessions'),
        {
          price: priceId,
          success_url: `${baseURL}/home`,
          cancel_url: `${baseURL}/pricing`,
          locale: 'pl', // Dodanie języka polskiego do sesji checkout
          tax_id_collection: { enabled: true },
          createdAt: new Date(),
          //payment_method_types: ['card', 'blik'],
        }
      );
      setCheckoutSessionId(docRef.id);
    } catch (error) {
      console.error('Błąd tworzenia sesji checkout:', error);
      toast.error('Nie udało się utworzyć sesji Stripe. Spróbuj ponownie.', {
        position: 'bottom-center',
      });
      setIsLoading(false); // Wyłącz ładowanie w przypadku błędu
    }
  };

  if (isCurrent) {
    return <ActiveButton disabled>Bieżąca subskrypcja</ActiveButton>;
  }

  if (isFuture) {
    return <ActiveButton disabled>Przyszła subskrypcja</ActiveButton>;
  }

  if (hasActiveSubscription) {
    return (
      <ManageButton onClick={onManageSubscription}>
        Zarządzaj subskrypcją
      </ManageButton>
    );
  }

  return (
    <StyledButton isCheckout onClick={handleClick}>
      {isLoading ? <Spinner /> : "Wybierz plan"}
    </StyledButton>
  );
}